<script lang="ts">
  import { onMount } from 'svelte';

  import { lazyLoad } from '$lib/actions/lazyLoad';

  /**
   * @typedef {Object} ImageProps
   * @property {string} src
   * @property {string} alt
   * @property {boolean} [priority]
   * @property {boolean} [fit]
   *
   * For consuming Image Component,
   * create a wrapping div component with properties like width, height, etc.
   *
   * @example
   * <div style="width: 100px; height: 100px; border-radius: 50%;">
   *  <Image src="..." alt="..." />
   * </div>
   *
   */

  type ImageFit = 'cover' | 'contain';

  export let src: string;
  export let alt: string;
  export let priority = false;

  export let imageFit: ImageFit = 'cover';
  export let width = '100%';
  export let height = '100%';
  let isSupported = false;
  onMount(() => (isSupported = 'loading' in HTMLImageElement.prototype));
</script>

{#if priority}
  <img
    {src}
    {alt}
    class="image"
    style:object-fit={imageFit}
    width={width ?? '100%'}
    height={height ?? '100%'}
    decoding="sync"
    loading="eager"
  />
{:else if isSupported}
  <img {src} {alt} loading="lazy" class="image" decoding="sync" style:object-fit={imageFit} />
{:else}
  <img
    use:lazyLoad={src}
    {alt}
    class="lazy-load image"
    decoding="sync"
    style:object-fit={imageFit}
  />
{/if}

<style>
  .lazy-load {
    opacity: 0;
    transition: opacity 0.3s ease-in-out;
  }

  .image {
    width: 100%;
    height: 100%;
  }
</style>
